import { computed, ref } from 'vue'
import {
  getMe,
  login as apiLogin,
  register as apiRegister,
  type SessionAssurance,
  type SessionResponse,
  type UserProfile,
} from '@/modules/auth/authApi'
import { clearLocalSession, hasSession, revokeLocalSession, setLocalSession } from '@/lib/session'
import { queryClient } from '@/lib/query'

/**
 * Compte connecté — état **purement client** : la session (jetons) appartient à
 * `@/lib/session`, ce module ne mémorise que le profil hydraté, l'assurance de la
 * session telle que le serveur la dit, et un miroir synchrone de « la session est vivante ».
 *
 * Refs de module, partagées par tous les appelants : un seul utilisateur par onglet.
 */
const user = ref<UserProfile | null>(null)

/**
 * Assurance de la session (`loa` dérivé de l'`amr` signé, politique MFA) — lue à chaque
 * hydratation et après toute montée de niveau. C'est elle que la garde de routeur consulte.
 */
const assurance = ref<SessionAssurance | null>(null)

/**
 * Miroir **synchrone** de `hasSession()`, pour les `computed` et les templates. Réaligné
 * explicitement (`refreshSession()`) aux moments qui comptent : garde de routeur,
 * hydratation, déconnexion.
 */
const sessionAlive = ref(false)

function applySession(session: SessionResponse): void {
  setLocalSession(session)
  sessionAlive.value = true
  user.value = session.user
  assurance.value = session.assurance
}

/**
 * **Point d'entrée unique** de l'hydratation : lit `GET /api/auth/me`. Appelé au
 * rechargement de page par la garde de routeur. Un échec purge l'état applicatif mais
 * **ne révoque pas** la session : une API momentanément indisponible ne justifie pas
 * une reconnexion complète.
 */
async function hydrate(): Promise<UserProfile> {
  try {
    const me = await getMe()
    user.value = me.user
    assurance.value = me.assurance
    sessionAlive.value = true
    return me.user
  } catch (error) {
    clearLocalState()
    throw error
  }
}

/**
 * Relit l'assurance de la session. À appeler après toute montée de niveau (facteur
 * vérifié). Un échec laisse l'état précédent — mieux vaut une assurance
 * ancienne qu'une page MFA imposée par erreur.
 */
async function refreshAssurance(): Promise<SessionAssurance | null> {
  try {
    const me = await getMe()
    user.value = me.user
    assurance.value = me.assurance
  } catch {
    /* état précédent conservé */
  }
  return assurance.value
}

async function tryLogin(credentials: { email: string; password: string }): Promise<SessionResponse> {
  const session = await apiLogin(credentials)
  applySession(session)
  return session
}

async function tryRegister(input: {
  email: string
  password: string
  firstName: string
  lastName: string
}): Promise<UserProfile> {
  applySession(await apiRegister(input))
  return user.value!
}

function clearLocalState(): void {
  user.value = null
  assurance.value = null
  sessionAlive.value = false
  queryClient.clear()
}

/**
 * Déconnexion complète : révocation **serveur d'abord** (la famille de refresh tokens
 * de cet appareil meurt), puis purge locale et retour à la mire.
 * ⚠️ Rien ne doit s'exécuter après cet appel : il déclenche une navigation.
 */
async function logout(): Promise<void> {
  clearLocalState()
  await revokeLocalSession()
  clearLocalSession()
  window.location.assign('/auth/login')
}

/** Réaligne le miroir synchrone sur la vérité de la session (garde de routeur). */
function refreshSession(): boolean {
  sessionAlive.value = hasSession()
  if (!sessionAlive.value && user.value) clearLocalState()
  return sessionAlive.value
}

export function useUser() {
  // Profil hydraté **et** session non expirée : le profil seul survivrait à la
  // révocation ; la session seule ne dit rien du rôle, dont dépend le cloisonnement.
  const isAuthenticated = computed(() => !!user.value && sessionAlive.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const displayName = computed(() =>
    user.value ? `${user.value.firstName} ${user.value.lastName}`.trim() || user.value.email : '',
  )
  /**
   * La politique **exige** un facteur et la session ne l'a pas prouvé : toute page protégée
   * ramène à la page second facteur. Inconnu (`null`) ⇒ on ne bloque pas.
   */
  const mfaBlocking = computed(
    () => !!assurance.value && assurance.value.loa < 2 && assurance.value.mfa.required,
  )
  /** Session tolérée à LoA 1 (« plus tard ») : un bandeau propose de renforcer la connexion. */
  const restrictedAccess = computed(
    () => !!assurance.value && assurance.value.loa < 2 && !assurance.value.mfa.required,
  )

  return {
    user,
    assurance,
    isAuthenticated,
    isAdmin,
    displayName,
    mfaBlocking,
    restrictedAccess,
    tryLogin,
    tryRegister,
    hydrate,
    refreshAssurance,
    logout,
    refreshSession,
    /** Après une modification du profil (PATCH /auth/me), sans réhydratation. */
    setUser: (next: UserProfile) => {
      user.value = next
    },
  }
}
